/**
 * The review rail: comments and tracked changes for the open file or the whole
 * project, with filters, next/previous, bulk accept/reject (undoable in the
 * open file) and "new since you last looked" markers.
 */
import { Check, ChevronDown, ChevronUp, FileText, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "../components/ui";
import { changesMap, threadsMap } from "../editor/collab";
import {
  acceptAll,
  addComment,
  deleteMessage,
  deleteThread,
  editMessage,
  entryIds,
  groupReplacements,
  rejectAll,
  replyToThread,
  reviewItems,
  setResolved,
  type ReviewEntry,
  type ReviewItem,
  type ThreadMessage,
} from "../editor/review";
import { api, type User } from "../lib/api";
import { formatDate, timeAgo, toast, useLocalStorage } from "../lib/app";
import type { DocHandle, ProjectSession } from "./session";

export interface DraftComment {
  from: number;
  to: number;
  quote: string;
}

export interface ReviewFile {
  docId: string;
  path: string;
  items: ReviewItem[];
}

type FileRef = { docId: string; path: string };

// ---------------------------------------------------------------- data

/** Live review items of one open doc. */
export function useReviewItems(handle: DocHandle | null): ReviewItem[] {
  const [items, setItems] = useState<ReviewItem[]>([]);
  useEffect(() => {
    if (!handle) {
      setItems([]);
      return;
    }
    let timer: number | undefined;
    const update = () => {
      clearTimeout(timer);
      timer = window.setTimeout(() => setItems(reviewItems(handle.ydoc, handle.ytext)), 120);
    };
    setItems(reviewItems(handle.ydoc, handle.ytext));
    handle.ydoc.on("update", update);
    return () => {
      clearTimeout(timer);
      handle.ydoc.off("update", update);
    };
  }, [handle]);
  return items;
}

/** Comments and tracked changes in every file, from the server. Not fetched while `paused` (nobody is using the page). */
export function useReviewSummary(projectId: string, handle: DocHandle | null, paused = false) {
  const [files, setFiles] = useState<ReviewFile[] | null>(null);
  const soon = useRef<number | undefined>(undefined);
  const refresh = useCallback(() => {
    api
      .get<{ files: ReviewFile[] }>(`/api/projects/${projectId}/review`)
      .then((r) => setFiles(r.files))
      .catch(() => {});
  }, [projectId]);
  // After a local change, give the server a moment to receive it.
  const refreshSoon = useCallback(() => {
    clearTimeout(soon.current);
    soon.current = window.setTimeout(refresh, 1500);
  }, [refresh]);

  useEffect(() => {
    if (paused) return;
    refresh();
    const timer = window.setInterval(() => document.visibilityState === "visible" && refresh(), 45_000);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      clearTimeout(soon.current);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh, paused]);

  useEffect(() => {
    if (!handle) return;
    const changes = changesMap(handle.ydoc);
    const threads = threadsMap(handle.ydoc);
    changes.observe(refreshSoon);
    threads.observeDeep(refreshSoon);
    return () => {
      changes.unobserve(refreshSoon);
      threads.unobserveDeep(refreshSoon);
    };
  }, [handle, refreshSoon]);

  /** Show a file's items straight after changing them here. */
  const setFileItems = useCallback(
    (docId: string, items: ReviewItem[]) => {
      setFiles((fs) => fs && fs.map((f) => (f.docId === docId ? { ...f, items } : f)));
      refreshSoon();
    },
    [refreshSoon],
  );
  return { files, setFileItems };
}

/** The server's list with the open file's live items swapped in. */
export function withLiveFile(files: ReviewFile[] | null, open: FileRef | null, live: ReviewItem[] | null): ReviewFile[] | null {
  if (!files || !open || !live) return files;
  const rest = files.filter((f) => f.docId !== open.docId);
  if (live.length) rest.push({ ...open, items: live });
  return rest.sort((a, b) => a.path.localeCompare(b.path));
}

// ---------------------------------------------------------------- unread

/** Who did the latest thing on an item, and when: the last reply, for a comment. */
function activity(item: ReviewItem): { userId: number; ts: number } {
  const last = item.messages?.[item.messages.length - 1];
  return last ? { userId: last.userId, ts: last.ts } : { userId: item.userId, ts: item.ts };
}

export interface ReviewSeen {
  isNew: (item: ReviewItem) => boolean;
  markRead: (items: ReviewItem[]) => void;
  markAllRead: () => void;
}

/** Per-person, per-browser memory of which comments and changes have been looked at. */
export function useReviewSeen(projectId: string, userId: number): ReviewSeen {
  const [seen, setSeen] = useLocalStorage<{ since: number; ids: Record<string, number> } | null>(`benleaf:reviewSeen:${projectId}`, null);
  // The first time, everything already there counts as read.
  useEffect(() => {
    if (!seen) setSeen({ since: Date.now(), ids: {} });
  }, [seen, setSeen]);

  return useMemo(() => {
    const since = seen?.since ?? Date.now();
    const ids = seen?.ids ?? {};
    const isNew = (item: ReviewItem) => {
      const a = activity(item);
      return a.userId !== userId && a.ts > Math.max(since, ids[item.id] ?? 0);
    };
    return {
      isNew,
      markRead: (items) => {
        const fresh = items.filter(isNew);
        if (!fresh.length) return;
        setSeen((prev) => {
          const next = { ...(prev?.ids ?? {}) };
          for (const item of fresh) next[item.id] = activity(item).ts;
          // Keep the most recent few hundred.
          const kept = Object.entries(next).sort((a, b) => b[1] - a[1]).slice(0, 300);
          return { since: prev?.since ?? since, ids: Object.fromEntries(kept) };
        });
      },
      markAllRead: () => setSeen({ since: Date.now(), ids: {} }),
    };
  }, [seen, setSeen, userId]);
}

/** Open comments and changes across the project, and how many are new. */
export function reviewCounts(files: ReviewFile[], seen: ReviewSeen) {
  let open = 0;
  let unread = 0;
  for (const f of files) {
    for (const e of groupReplacements(f.items)) {
      if (e.resolved) continue;
      open++;
      if (seen.isNew(e) || (e.replaced && seen.isNew(e.replaced))) unread++;
    }
  }
  return { open, unread };
}

// ---------------------------------------------------------------- panel

type Scope = "file" | "project";
type Show = "all" | "comments" | "changes";
interface Located {
  file: FileRef;
  entry: ReviewEntry;
}

const entryKind = (e: ReviewEntry) => (e.replaced ? "replace" : e.kind);
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Enter sends, Shift+Enter starts a new line, Escape cancels. */
const sendOnEnter = (send: () => void, cancel?: () => void) => (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
    e.preventDefault();
    send();
  } else if (e.key === "Escape" && cancel) {
    e.preventDefault();
    cancel();
  }
};

export function ReviewPanel({
  session,
  handle,
  open,
  items,
  files,
  user,
  canEdit,
  tracking,
  focusedId,
  draft,
  seen,
  navRequest,
  cursor,
  onDraftDone,
  onFocus,
  onFileItems,
  onClose,
}: {
  session: ProjectSession;
  /** The open file's live doc. */
  handle: DocHandle | null;
  open: FileRef | null;
  items: ReviewItem[];
  /** Every file with comments or changes (open file included), or null while loading. */
  files: ReviewFile[] | null;
  user: User;
  canEdit: boolean;
  tracking: boolean;
  focusedId: string | null;
  draft: DraftComment | null;
  seen: ReviewSeen;
  /** Bumped by the editor's next/previous keys. */
  navRequest: { dir: 1 | -1; n: number } | null;
  /** Cursor position in the open file. */
  cursor: () => number | null;
  onDraftDone: () => void;
  /** Select an item in the editor, opening its file if needed. */
  onFocus: (file: FileRef, item: ReviewItem) => void;
  onFileItems: (docId: string, items: ReviewItem[]) => void;
  onClose: () => void;
}) {
  const [scope, setScope] = useLocalStorage<Scope>("benleaf:reviewScope", "file");
  const [show, setShow] = useLocalStorage<Show>("benleaf:reviewShow", "all");
  const [status, setStatus] = useState<"open" | "resolved">("open");
  const [author, setAuthor] = useState<number | null>(null);
  const [draftText, setDraftText] = useState("");
  const [undo, setUndo] = useState<{ text: string; docId: string; depth: number } | null>(null);
  const me = { id: user.id, name: user.displayName };

  // Every entry in scope, by file.
  const groups = useMemo(() => {
    const src: ReviewFile[] = scope === "file" ? (open ? [{ ...open, items }] : []) : (files ?? []);
    return src.map((f) => ({ file: { docId: f.docId, path: f.path }, entries: groupReplacements(f.items) }));
  }, [scope, open?.docId, open?.path, items, files]); // eslint-disable-line react-hooks/exhaustive-deps

  const authors = useMemo(() => {
    const m = new Map<number, string>();
    for (const g of groups) for (const e of g.entries) m.set(e.userId, e.userName);
    return [...m].sort((a, b) => a[1].localeCompare(b[1]));
  }, [groups]);
  const who = author !== null && authors.some(([id]) => id === author) ? author : null;

  const passes = (e: ReviewEntry) => (show === "all" || (show === "comments") === (e.kind === "comment")) && (who === null || e.userId === who);
  const inTab = (e: ReviewEntry, tab: "open" | "resolved") => (tab === "open" ? !e.resolved : e.kind === "comment" && !!e.resolved);
  const count = (tab: "open" | "resolved") => groups.reduce((n, g) => n + g.entries.filter((e) => passes(e) && inTab(e, tab)).length, 0);
  const visible = groups.map((g) => ({ ...g, entries: g.entries.filter((e) => passes(e) && inTab(e, status)) })).filter((g) => g.entries.length);
  const flat: Located[] = visible.flatMap((g) => g.entries.map((entry) => ({ file: g.file, entry })));
  const changes = status === "open" ? flat.filter((l) => l.entry.kind !== "comment") : [];
  const changeFiles = new Set(changes.map((l) => l.file.docId)).size;
  const unread = flat.filter((l) => seen.isNew(l.entry)).length;
  const isFocused = (e: ReviewEntry) => !!focusedId && entryIds(e).includes(focusedId);

  // Bring the focused card into view and count it as read.
  useEffect(() => {
    if (!focusedId) return;
    document.querySelector(`[data-review-ids~="${CSS.escape(focusedId)}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const hit = flat.find((l) => isFocused(l.entry));
    if (hit) seen.markRead(hit.entry.replaced ? [hit.entry, hit.entry.replaced] : [hit.entry]);
  }, [focusedId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setDraftText(""), [draft]);
  useEffect(() => {
    if (!undo) return;
    const t = window.setTimeout(() => setUndo(null), 12_000);
    return () => clearTimeout(t);
  }, [undo]);

  // ------------------------------------------------------------ navigation

  const go = (dir: 1 | -1) => {
    if (!flat.length) {
      toast(status === "open" ? "No open comments or changes here" : "No resolved comments here");
      return;
    }
    let i = flat.findIndex((l) => isFocused(l.entry));
    if (i !== -1) i = (i + dir + flat.length) % flat.length;
    else {
      // Nothing selected: go from the cursor.
      const here = flat.map((l, n) => ({ l, n })).filter(({ l }) => l.file.docId === open?.docId);
      const pos = cursor() ?? 0;
      if (dir === 1) i = here.find(({ l }) => l.entry.from > pos)?.n ?? (here.length ? (here[here.length - 1].n + 1) % flat.length : 0);
      else i = [...here].reverse().find(({ l }) => l.entry.from < pos)?.n ?? (here.length ? (here[0].n - 1 + flat.length) % flat.length : flat.length - 1);
    }
    onFocus(flat[i].file, flat[i].entry);
  };
  const lastNav = useRef(0);
  useEffect(() => {
    if (!navRequest || navRequest.n === lastNav.current) return;
    lastNav.current = navRequest.n;
    go(navRequest.dir);
  }, [navRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------ actions

  /**
   * Run a review edit on a file's doc. In the open file, accepting and
   * rejecting land on the undo stack as their own step (comments don't);
   * other files are opened in the background.
   */
  const run = async (docId: string, label: string, fn: (h: DocHandle) => void) => {
    if (handle && docId === handle.docId) {
      const um = handle.undoManager;
      const before = um.undoStack.length;
      um.stopCapturing();
      fn(handle);
      um.stopCapturing();
      if (um.undoStack.length > before) setUndo({ text: label, docId, depth: um.undoStack.length });
      return;
    }
    const h = session.openDoc(docId, true);
    await h.synced;
    if (h.readOnly) throw new Error("read-only");
    fn(h);
    onFileItems(docId, reviewItems(h.ydoc, h.ytext));
  };

  const undoLast = () => {
    if (!undo) return;
    setUndo(null);
    const um = handle?.docId === undo.docId ? handle.undoManager : null;
    if (um && um.undoStack.length === undo.depth) um.undo();
    else toast("Can't undo that here any more. Use History to get old text back.");
  };

  /** After acting on the selected entry, select the next one in the same file. */
  const advance = (from: Located) => {
    if (!isFocused(from.entry) || !handle || from.file.docId !== handle.docId) return;
    const i = flat.findIndex((l) => l.entry.id === from.entry.id);
    const next = [...flat.slice(i + 1), ...flat.slice(0, Math.max(0, i)).reverse()].find((l) => l.file.docId === from.file.docId && l.entry.id !== from.entry.id);
    const fresh = next && reviewItems(handle.ydoc, handle.ytext).find((it) => it.id === next.entry.id);
    if (fresh) onFocus(from.file, fresh);
  };

  const describe = (e: ReviewEntry) => ({ ins: "addition", del: "deletion", replace: "replacement", comment: "comment" })[entryKind(e)];

  const decide = (l: Located, accept: boolean) => {
    const ids = entryIds(l.entry);
    run(l.file.docId, `${accept ? "Accepted" : "Rejected"} ${describe(l.entry)}`, (h) => (accept ? acceptAll(h.ydoc, ids) : rejectAll(h.ydoc, h.ytext, ids))).catch(failed);
    advance(l);
  };

  const decideAll = async (accept: boolean) => {
    const byDoc = new Map<string, string[]>();
    for (const l of changes) byDoc.set(l.file.docId, [...(byDoc.get(l.file.docId) ?? []), ...entryIds(l.entry)]);
    const elsewhere = [...byDoc.keys()].some((id) => id !== handle?.docId);
    if (elsewhere) {
      const verb = accept ? "Accept" : "Reject";
      const extra = accept ? "" : " Rejected additions are removed and deleted text comes back.";
      if (!confirm(`${verb} ${plural(changes.length, "change")} in ${plural(changeFiles, "file")}?${extra} Only changes in the open file can be undone here.`)) return;
    }
    const label = `${accept ? "Accepted" : "Rejected"} ${plural(changes.length, "change")}`;
    try {
      for (const [docId, ids] of byDoc) await run(docId, label, (h) => (accept ? acceptAll(h.ydoc, ids) : rejectAll(h.ydoc, h.ytext, ids)));
      if (elsewhere) toast(label, "success");
    } catch (e) {
      failed(e);
    }
  };

  const comment = (l: Located, label: string, fn: (h: DocHandle) => void) => run(l.file.docId, label, fn).catch(failed);
  const failed = (e: unknown) => toast(e instanceof Error && e.message === "read-only" ? "You can only read this project." : "Couldn't change that. Try again.", "error");

  const sendDraft = () => {
    if (!draft || !handle || !draftText.trim()) return;
    addComment(handle.ydoc, handle.ytext, draft.from, draft.to, me, draftText.trim());
    onDraftDone();
  };

  // ------------------------------------------------------------ render

  const card = (l: Located) => (
    <ReviewCard
      key={l.entry.id}
      entry={l.entry}
      me={me}
      canEdit={canEdit}
      focused={isFocused(l.entry)}
      isNew={seen.isNew(l.entry)}
      onFocus={() => onFocus(l.file, l.entry)}
      onAccept={() => decide(l, true)}
      onReject={() => decide(l, false)}
      onResolve={(resolved) => {
        comment(l, resolved ? "Resolved comment" : "Reopened comment", (h) => setResolved(h.ydoc, l.entry.id, resolved, me));
        if (resolved) advance(l);
      }}
      onReply={(text) => comment(l, "Replied", (h) => replyToThread(h.ydoc, l.entry.id, me, text))}
      onDelete={() => confirm("Delete this comment and its replies?") && comment(l, "Deleted comment", (h) => deleteThread(h.ydoc, l.entry.id))}
      onEditMessage={(m, text) => comment(l, "Edited comment", (h) => editMessage(h.ydoc, l.entry.id, m.id, text))}
      onDeleteMessage={(m) => confirm("Delete this reply?") && comment(l, "Deleted reply", (h) => deleteMessage(h.ydoc, l.entry.id, m.id))}
    />
  );

  return (
    <aside className="review-rail">
      <div className="review-head">
        <div className="review-seg" role="tablist">
          <button className={scope === "file" ? "on" : ""} onClick={() => setScope("file")} title="Comments and changes in the open file">
            This file
          </button>
          <button className={scope === "project" ? "on" : ""} onClick={() => setScope("project")} title="Comments and changes in every file">
            All files
          </button>
        </div>
        <span className="grow" />
        <button className="icon-btn" onClick={() => go(-1)} disabled={!flat.length} title="Previous (Ctrl/⌘+Alt+P)">
          <ChevronUp size={16} />
        </button>
        <button className="icon-btn" onClick={() => go(1)} disabled={!flat.length} title="Next (Ctrl/⌘+Alt+N)">
          <ChevronDown size={16} />
        </button>
        <button className="icon-btn" onClick={onClose} title="Close review panel">
          <X size={16} />
        </button>
      </div>
      <div className="review-head review-filters">
        <div className="review-tabs">
          <button className={status === "open" ? "on" : ""} onClick={() => setStatus("open")}>
            Open {count("open")}
          </button>
          <button className={status === "resolved" ? "on" : ""} onClick={() => setStatus("resolved")}>
            Resolved {count("resolved")}
          </button>
        </div>
        <span className="grow" />
        <select value={show} onChange={(e) => setShow(e.target.value as Show)} title="What to show" aria-label="What to show">
          <option value="all">All</option>
          <option value="comments">Comments</option>
          <option value="changes">Changes</option>
        </select>
        {(authors.length > 1 || who !== null) && (
          <select value={who ?? ""} onChange={(e) => setAuthor(e.target.value ? Number(e.target.value) : null)} title="Whose" aria-label="Whose">
            <option value="">Anyone</option>
            {authors.map(([id, name]) => (
              <option key={id} value={id}>
                {id === user.id ? `${name} (you)` : name}
              </option>
            ))}
          </select>
        )}
      </div>
      {canEdit && changes.length > 0 && (
        <div className="review-bar">
          <span className="grow">
            {plural(changes.length, "change")}
            {changeFiles > 1 && ` in ${changeFiles} files`}
          </span>
          <button className="btn btn-sm" onClick={() => decideAll(false)}>
            Reject all
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => decideAll(true)}>
            Accept all
          </button>
        </div>
      )}
      {undo && (
        <div className="review-bar review-undo">
          <span className="grow">{undo.text}</span>
          <button className="btn btn-sm btn-ghost" onClick={undoLast}>
            Undo
          </button>
        </div>
      )}
      {unread > 0 && (
        <div className="review-bar review-new">
          <span className="new-dot" />
          <span className="grow">{unread} new since you last looked</span>
          <button className="btn btn-sm btn-ghost" onClick={seen.markAllRead}>
            Mark read
          </button>
        </div>
      )}
      <div className="review-list">
        {draft && handle && (
          <div className="review-card kind-comment focused">
            <div className="quote">{draft.quote}</div>
            <textarea autoFocus placeholder="Add a comment…" value={draftText} onChange={(e) => setDraftText(e.target.value)} onKeyDown={sendOnEnter(sendDraft, onDraftDone)} />
            <div className="actions">
              <span className="hint grow">Enter to send · Shift+Enter for a new line</span>
              <button className="btn btn-sm" onClick={onDraftDone}>
                Cancel
              </button>
              <button className="btn btn-sm btn-primary" disabled={!draftText.trim()} onClick={sendDraft}>
                Comment
              </button>
            </div>
          </div>
        )}
        {scope === "project" && !files && <div className="review-empty">Loading…</div>}
        {flat.length === 0 && !draft && (scope === "file" || files) && (
          <div className="review-empty">
            {status === "resolved" ? (
              "No resolved comments."
            ) : show !== "all" || who !== null ? (
              <>
                Nothing matches these filters.
                <br />
                <button className="btn btn-sm btn-ghost" onClick={() => (setShow("all"), setAuthor(null))}>
                  Show everything
                </button>
              </>
            ) : (
              <>
                No comments or tracked changes {scope === "file" ? "in this file" : "in this project"}.
                <br />
                <br />
                Select text and press <kbd>💬 Comment</kbd> (or <kbd>Ctrl/⌘+Alt+M</kbd>) to comment.
                {tracking ? " Your edits are being tracked." : " Turn on Track changes to suggest edits."}
              </>
            )}
          </div>
        )}
        {scope === "file"
          ? flat.map(card)
          : visible.map((g) => (
              <section key={g.file.docId} className="review-file">
                <button
                  className={`review-file-head${g.file.docId === open?.docId ? " current" : ""}`}
                  onClick={() => onFocus(g.file, g.entries[0])}
                  title={g.file.docId === open?.docId ? "The open file" : `Open ${g.file.path}`}
                >
                  <FileText size={13} />
                  <span className="grow">{g.file.path}</span>
                  <span className="muted small">{g.entries.length}</span>
                </button>
                {g.entries.map((entry) => card({ file: g.file, entry }))}
              </section>
            ))}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------- cards

function ReviewCard({
  entry,
  me,
  canEdit,
  focused,
  isNew,
  onFocus,
  onAccept,
  onReject,
  onResolve,
  onReply,
  onDelete,
  onEditMessage,
  onDeleteMessage,
}: {
  entry: ReviewEntry;
  me: { id: number; name: string };
  canEdit: boolean;
  focused: boolean;
  isNew: boolean;
  onFocus: () => void;
  onAccept: () => void;
  onReject: () => void;
  onResolve: (resolved: boolean) => void;
  onReply: (text: string) => void;
  onDelete: () => void;
  onEditMessage: (m: ThreadMessage, text: string) => void;
  onDeleteMessage: (m: ThreadMessage) => void;
}) {
  const [reply, setReply] = useState("");
  const kind = entryKind(entry);
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };
  const send = () => {
    if (!reply.trim()) return;
    onReply(reply.trim());
    setReply("");
  };

  return (
    <div
      className={`review-card kind-${kind}${focused ? " focused" : ""}${entry.resolved ? " resolved" : ""}`}
      data-review-ids={entryIds(entry).join(" ")}
      onClick={onFocus}
    >
      <div className="who">
        <Avatar id={entry.userId} name={entry.userName} size={20} />
        <b>{entry.userName}</b>
        <span className="muted small" title={formatDate(entry.ts)}>
          {timeAgo(entry.ts)}
        </span>
        <span className="grow" />
        {isNew && <span className="new-dot" title="New since you last looked" />}
        <span className="line-chip" title={`Line ${entry.line}`}>
          L{entry.line}
        </span>
        {canEdit && kind === "comment" && !entry.resolved && (
          <button className="icon-btn icon-btn-sm" title="Resolve" onClick={stop(() => onResolve(true))}>
            <Check size={14} />
          </button>
        )}
      </div>
      {kind === "ins" && (
        <>
          <span className="label">Added</span>
          <div className="quote ins">{entry.text}</div>
        </>
      )}
      {kind === "del" && (
        <>
          <span className="label">Deleted</span>
          <div className="quote del">{entry.text}</div>
        </>
      )}
      {kind === "replace" && (
        <>
          <span className="label">Replaced</span>
          <div className="quote del">{entry.replaced!.text}</div>
          <span className="label">with</span>
          <div className="quote ins">{entry.text}</div>
        </>
      )}
      {kind === "comment" && (
        <>
          {entry.text && <div className="quote">{entry.text}</div>}
          {entry.messages?.map((m, i) => (
            <Message
              key={m.id}
              m={m}
              showName={m.userId !== entry.userId}
              mine={canEdit && m.userId === me.id}
              onEdit={(text) => onEditMessage(m, text)}
              onDelete={i > 0 ? () => onDeleteMessage(m) : undefined}
            />
          ))}
          {entry.resolved && <div className="muted small resolved-by">Resolved{entry.resolvedBy ? ` by ${entry.resolvedBy}` : ""}</div>}
        </>
      )}
      {canEdit && kind !== "comment" && (
        <div className="actions">
          <button className="btn btn-sm" onClick={stop(onReject)} title="Undo this edit">
            Reject
          </button>
          <button className="btn btn-sm btn-primary" onClick={stop(onAccept)} title="Keep this edit">
            Accept
          </button>
        </div>
      )}
      {canEdit && kind === "comment" && focused && (
        <div onClick={(e) => e.stopPropagation()}>
          {!entry.resolved && (
            <textarea placeholder="Reply…" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={sendOnEnter(send, () => setReply(""))} />
          )}
          <div className="actions">
            <button className="btn btn-sm btn-ghost" onClick={onDelete}>
              Delete
            </button>
            <span className="grow" />
            <button className="btn btn-sm" onClick={() => onResolve(!entry.resolved)}>
              {entry.resolved ? "Reopen" : "Resolve"}
            </button>
            {!entry.resolved && (
              <button className="btn btn-sm btn-primary" disabled={!reply.trim()} onClick={send}>
                Reply
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Message({ m, showName, mine, onEdit, onDelete }: { m: ThreadMessage; showName: boolean; mine: boolean; onEdit: (text: string) => void; onDelete?: () => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(m.text);
  const save = () => {
    if (text.trim()) onEdit(text.trim());
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="msg" onClick={(e) => e.stopPropagation()}>
        <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} onKeyDown={sendOnEnter(save, () => setEditing(false))} />
        <div className="actions">
          <button className="btn btn-sm" onClick={() => setEditing(false)}>
            Cancel
          </button>
          <button className="btn btn-sm btn-primary" disabled={!text.trim()} onClick={save}>
            Save
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="msg">
      {showName && <b>{m.userName}: </b>}
      {m.text}
      {m.edited && (
        <span className="muted small" title={`Edited ${formatDate(m.edited)}`}>
          {" "}
          (edited)
        </span>
      )}
      {mine && (
        <span className="msg-tools">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setText(m.text);
              setEditing(true);
            }}
          >
            Edit
          </button>
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              Delete
            </button>
          )}
        </span>
      )}
    </div>
  );
}
